/**
 * LinkedNoteDialog — identical to the Add Note dialog on the Horse Overview tab,
 * but with a read-only "Linked Care Entry" reference shown at the top.
 */
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Camera, RotateCw, X, File } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import HorsePickerInline from './HorsePickerInline';

const NOTE_TYPE_OPTIONS = [
  { value: 'conformation_note',     label: 'Conformation Note' },
  { value: 'vet_procedure_note',    label: 'Vet Procedure Note' },
  { value: 'layup_note',            label: 'Layup Note' },
  { value: 'sales_inspection_note', label: 'Sales Inspection Note' },
  { value: 'other_note',            label: 'Other Note' },
];

const EMPTY = {
  note_type: 'other_note',
  date: new Date().toLocaleDateString('sv'),
  by: '',
  title: '',
  notes: '',
  grade: '',
  picture_url: '',
  picture_file_id: '',
  file_url: '',
  file_name: '',
  file_id: '',
  publish_to_portal: false,
};

export default function LinkedNoteDialog({ open, onClose, onSaved, horse, careEntryRef, recentCareLog, availableHorses }) {
  const [form, setForm]                 = useState({ ...EMPTY });
  const [saving, setSaving]             = useState(false);
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingFile, setUploadingFile]       = useState(false);
  const [picturePreview, setPicturePreview]     = useState(null);
  const pictureInputRef = useRef();
  const fileInputRef    = useRef();

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePictureUpload = async (file) => {
    setUploadingPicture(true);
    try {
      const preview = URL.createObjectURL(file);
      setPicturePreview(preview);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'note_photo');
      fd.append('horse_id', horse.id);
      const res = await api.post('/files/upload', fd);
      f('picture_url', `/api/files/public/${res.data.id}`);
      f('picture_file_id', res.data.id);
    } catch { toast.error('Photo upload failed'); }
    setUploadingPicture(false);
  };

  const handleFileUpload = async (file) => {
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'note_file');
      fd.append('horse_id', horse.id);
      const res = await api.post('/files/upload', fd);
      f('file_url', res.data.url || '');
      f('file_name', file.name);
      f('file_id', res.data.id);
    } catch { toast.error('File upload failed'); }
    setUploadingFile(false);
  };

  const [rotatingPicture, setRotatingPicture] = useState(false);

  const handleRotateNotePicture = async () => {
    const src = picturePreview || form.picture_url;
    if (!src || rotatingPicture) return;
    setRotatingPicture(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalHeight;
          canvas.height = img.naturalWidth;
          const ctx = canvas.getContext('2d');
          ctx.translate(img.naturalHeight / 2, img.naturalWidth / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', 0.92);
        };
        img.onerror = reject;
        img.src = src;
      });
      const rotatedFile = new File([blob], 'note-photo-rotated.jpg', { type: 'image/jpeg' });
      // Delete old file before re-uploading
      if (form.picture_file_id) {
        await api.delete(`/files/${form.picture_file_id}`).catch(() => {});
      }
      await handlePictureUpload(rotatedFile);
    } catch {
      toast.error('Failed to rotate photo');
    } finally {
      setRotatingPicture(false);
    }
  };

  const handleRemovePicture = async () => {
    if (form.picture_file_id) {
      await api.delete(`/files/${form.picture_file_id}`).catch(() => {});
    }
    setPicturePreview(null);
    setForm(p => ({ ...p, picture_url: '', picture_file_id: '' }));
  };

  // Build effective careEntryRef: prefer selected entry over prop
  const effectiveCareRef = (() => {
    if (selectedEntryId && recentCareLog) {
      const e = recentCareLog.find(c => c.id === selectedEntryId);
      if (e) return `${e.type} — ${e.name} (${e.log_date}${e.log_time ? ' @ ' + e.log_time : ''})`;
    }
    return careEntryRef || '';
  })();

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        horse_id: horse.id,
        // Append care entry reference to notes
        notes: [
          form.notes,
          careEntryRef ? `\n[Linked care entry: ${effectiveCareRef}]` : effectiveCareRef ? `\n[Linked care entry: ${effectiveCareRef}]` : '',
        ].filter(Boolean).join(''),
      };
      if (payload.note_type !== 'conformation_note') payload.grade = '';
      if (Array.isArray(availableHorses)) {
        if (selectedHorseIds.length === 0) { toast.error('Select at least one horse'); setSaving(false); return; }
        await Promise.all(selectedHorseIds.map(hid =>
          api.post('/conformation-notes', { ...payload, horse_id: hid })
        ));
      } else {
        await api.post('/conformation-notes', payload);
      }
      toast.success('Note saved');
      setForm({ ...EMPTY });
      setPicturePreview(null);
      onSaved();
      onClose();
    } catch { toast.error('Failed to save note'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setForm({ ...EMPTY }); setPicturePreview(null); onClose(); } }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {Array.isArray(availableHorses) && (
            <HorsePickerInline availableHorses={availableHorses}
              value={selectedHorseIds} onChange={setSelectedHorseIds} mode="multi" />
          )}
          {/* Linked care entry reference or picker */}
          {recentCareLog && recentCareLog.length > 0 ? (
            <div className="space-y-1">
              <label className="text-sm font-medium">Link to Care Entry (optional)</label>
              <Select value={selectedEntryId || '__none__'} onValueChange={v => setSelectedEntryId(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select a care entry…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {recentCareLog.slice(0, 30).map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.type} — {e.name} ({e.log_date}{e.log_time ? ' @ ' + e.log_time : ''})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : effectiveCareRef ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded border bg-muted/20 text-xs text-muted-foreground">
              <span className="font-semibold shrink-0">Linked care entry:</span>
              <span>{effectiveCareRef}</span>
            </div>
          ) : null}

          {/* Note Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Note Type *</label>
            <Select value={form.note_type}
              onValueChange={v => setForm(p => ({ ...p, note_type: v, grade: v !== 'conformation_note' ? '' : p.grade }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {NOTE_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <Input type="date" value={form.date} onChange={e => f('date', e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">By</label>
              <Input value={form.by} onChange={e => f('by', e.target.value)} placeholder="Person's name" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Title *</label>
            <Input value={form.title} onChange={e => f('title', e.target.value)} placeholder="Brief title" />
          </div>

          {form.note_type === 'conformation_note' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Grade</label>
              <Input value={form.grade} onChange={e => f('grade', e.target.value)} placeholder="e.g., A, B+, Good" />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Notes</label>
            <Textarea value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Detailed notes..." rows={4} />
          </div>

          {/* Photo */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Photo</label>
            {(picturePreview || form.picture_url) ? (
              <div className="space-y-2">
                <div className="relative">
                  <img
                    src={picturePreview || (form.picture_url?.startsWith('http') ? form.picture_url : `${window.location.origin}${form.picture_url}`)}
                    alt="Preview" className="w-full h-auto rounded-lg border max-h-48 object-contain"
                  />
                  <Button variant="destructive" size="icon" className="absolute top-2 right-2 h-7 w-7"
                    onClick={handleRemovePicture} type="button">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {/* Rotate button — shown when photo is present */}
                <Button type="button" variant="outline" size="sm"
                  onClick={handleRotateNotePicture}
                  disabled={rotatingPicture || uploadingPicture}
                  data-testid="rotate-note-photo-btn">
                  <RotateCw className={`w-4 h-4 mr-2 ${rotatingPicture ? 'animate-spin' : ''}`} />
                  {rotatingPicture ? 'Rotating…' : 'Rotate'}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input ref={pictureInputRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const fl = e.target.files?.[0]; if (fl) { handlePictureUpload(fl); e.target.value = ''; } }} />
                <Button type="button" variant="outline" className="flex-1"
                  onClick={() => pictureInputRef.current?.click()} disabled={uploadingPicture || rotatingPicture}>
                  <Upload className="w-4 h-4 mr-2" />{uploadingPicture ? 'Uploading…' : 'Upload Photo'}
                </Button>
                <Button type="button" variant="outline"
                  onClick={() => { pictureInputRef.current.setAttribute('capture', 'environment'); pictureInputRef.current?.click(); }}
                  disabled={uploadingPicture || rotatingPicture}>
                  <Camera className="w-4 h-4 mr-2" />Take Photo
                </Button>
              </div>
            )}
          </div>

          {/* File */}
          <div className="space-y-2">
            <label className="text-sm font-medium">File</label>
            {form.file_id ? (
              <div className="flex items-center gap-2 p-2 border rounded bg-muted/30">
                <File className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm flex-1 truncate">{form.file_name}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" type="button"
                  onClick={() => setForm(p => ({ ...p, file_url: '', file_name: '', file_id: '' }))}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <>
                <input ref={fileInputRef} type="file" className="hidden"
                  onChange={e => { const fl = e.target.files?.[0]; if (fl) { handleFileUpload(fl); e.target.value = ''; } }} />
                <Button type="button" variant="outline" className="w-full"
                  onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                  <Upload className="w-4 h-4 mr-2" />{uploadingFile ? 'Uploading…' : 'Upload File'}
                </Button>
              </>
            )}
          </div>

          {/* Publish to portal */}
          <div className="pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" className="rounded" checked={form.publish_to_portal}
                onChange={e => f('publish_to_portal', e.target.checked)} />
              Publish to Owner Portal
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || uploadingPicture || uploadingFile}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : 'Save Note'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
